/** Named URL filter presets — plan/35 C (no second filter model). */

export type SavedListView = {
  id: string;
  name: string;
  /** `URLSearchParams.toString()` for this list. */
  qs: string;
};

export type SavedListViewsState = {
  views: SavedListView[];
};

export const SAVED_LIST_VIEWS_MAX = 12;

export function savedListViewsKey(
  listId: string,
  companySlug: string,
): string {
  return `icraftx.savedviews.${companySlug}.${listId}`;
}

export function loadSavedListViews(
  listId: string,
  companySlug: string,
): SavedListView[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(
      savedListViewsKey(listId, companySlug),
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedListViewsState;
    if (!parsed || !Array.isArray(parsed.views)) return [];
    return parsed.views.filter(
      (v) =>
        v &&
        typeof v.id === "string" &&
        typeof v.name === "string" &&
        typeof v.qs === "string",
    );
  } catch {
    return [];
  }
}

function persist(
  listId: string,
  companySlug: string,
  views: SavedListView[],
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      savedListViewsKey(listId, companySlug),
      JSON.stringify({ views } satisfies SavedListViewsState),
    );
  } catch {
    /* quota */
  }
}

export function saveListView(
  listId: string,
  companySlug: string,
  input: { name: string; qs: string; id?: string },
): SavedListView[] {
  const name = input.name.trim();
  if (!name) return loadSavedListViews(listId, companySlug);
  const views = loadSavedListViews(listId, companySlug);
  const id = input.id ?? `sv-${crypto.randomUUID()}`;
  const next: SavedListView = { id, name, qs: input.qs };
  const idx = views.findIndex((v) => v.id === id);
  let out: SavedListView[];
  if (idx >= 0) {
    out = views.map((v, i) => (i === idx ? next : v));
  } else {
    out = [next, ...views].slice(0, SAVED_LIST_VIEWS_MAX);
  }
  persist(listId, companySlug, out);
  return out;
}

export function deleteSavedListView(
  listId: string,
  companySlug: string,
  id: string,
): SavedListView[] {
  const out = loadSavedListViews(listId, companySlug).filter(
    (v) => v.id !== id,
  );
  persist(listId, companySlug, out);
  return out;
}
